import type { AppRouter } from '@markku/backend/trpc/router'
import type { AttachmentUpdateData, QueryOptions } from '@markku/types'
import type { GenericFilter } from '@markku/utils'
import type { CreateTRPCReact } from '@trpc/react-query'

interface HasId {
  id: number
}

interface ListResponse<T> {
  data: T[]
  pagination: {
    total: number
    offset: number
    limit: number
  }
}

type WithOptionalAttachments<T> = T & {
  attachments?: Array<
    | { id: number; action: 'add' | 'delete' }
    | { id: number; action: 'update'; data: AttachmentUpdateData }
  >
}

export interface ListParams<T> {
  page?: number
  pageSize?: number
  filters?: GenericFilter<T>
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
  search?: string
}

export interface UseListReturn<T> {
  data?: ListResponse<T>
  isLoading: boolean
  error: Error
  refetch: () => void
}

export interface UseGetByIdReturn<T> {
  data?: T
  isLoading: boolean
  error: Error
  // react-query resolves a refetch with its result instead of throwing, so a
  // caller that awaits one can tell a landed refetch from a failed one.
  refetch: () => Promise<{ data?: T; error: Error | null }>
}

export interface MutationOptions {
  showNotifications?: boolean
}

export interface UseMutationReturn<TInput, TOutput> {
  mutateAsync: (
    input: WithOptionalAttachments<TInput>,
    options?: MutationOptions,
  ) => Promise<TOutput>
  isPending: boolean
  error: Error
}

export interface CrudHooksConfig {
  trpc: CreateTRPCReact<AppRouter, unknown>
  resourceName: string
  displayName?: string
  showNotifications?: boolean
  relatedResources?: string[]
}

/**
 * Generic CRUD hook factory with full type safety.
 * Creates type-safe hooks for any tRPC resource.
 *
 * @param config - Configuration for the CRUD hooks
 * @returns Object containing fully typed useList, useGetById, useCreate, useUpdate, useDelete hooks
 */
export function createCrudHooks<
  TItem,
  TListResult = TItem,
  TCreateInput = Partial<TItem>,
  TUpdateInput = Partial<TItem>,
  TFilterType = TItem,
>(config: CrudHooksConfig) {
  const {
    trpc,
    resourceName,
    displayName,
    showNotifications = false,
    relatedResources = [],
  } = config

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resource = trpc[resourceName as keyof typeof trpc] as any
  const label = displayName || resourceName

  const showSuccessNotification = async (message: string) => {
    if (showNotifications) {
      const { notifications } = await import('@mantine/notifications')
      notifications.show({
        title: 'Onnistui',
        message,
        color: 'green',
      })
    }
  }

  const showErrorNotification = async (error: Error, action: string) => {
    if (showNotifications) {
      const { notifications } = await import('@mantine/notifications')
      notifications.show({
        title: 'Virhe',
        message: error.message || `${label} ${action} epäonnistui`,
        color: 'red',
      })
    }
  }

  const invalidateRelatedResources = (
    utils: ReturnType<typeof trpc.useUtils>,
  ) => {
    // A router ('hourSheet') or one of its procedures ('visit.myAvailabilityDays').
    relatedResources.forEach((name) => {
      const related = name
        .split('.')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((node: any, key) => node?.[key], utils)
      related?.invalidate()
    })
  }

  return {
    useList: (
      params: ListParams<TFilterType> = {},
      options?: QueryOptions,
    ): UseListReturn<TListResult> => {
      const {
        page = 1,
        pageSize = 100,
        filters = {},
        orderBy,
        orderDirection = 'asc',
        search,
      } = params
      const offset = (page - 1) * pageSize

      return resource.list.useQuery(
        {
          offset,
          limit: pageSize,
          filters,
          orderBy,
          orderDirection,
          ...(search ? { search } : {}),
        },
        { staleTime: 5 * 60 * 1000, ...options },
      )
    },

    useGetById: (
      id: number | string,
      options?: QueryOptions,
    ): UseGetByIdReturn<TItem> => {
      return resource.getById.useQuery(
        { id: Number(id) },
        { enabled: !!id, staleTime: 5 * 60 * 1000, ...options },
      )
    },

    useCreate: (): UseMutationReturn<{ data: TCreateInput }, TItem> => {
      const utils = trpc.useUtils()
      const baseMutation = resource.create.useMutation({
        onError: async (error: Error) => {
          await showErrorNotification(error, 'luonti')
        },
      })

      return {
        mutateAsync: async (input, options = { showNotifications: true }) => {
          const result = await baseMutation.mutateAsync(input)
          if (options.showNotifications !== false) {
            await showSuccessNotification(`${label} luotu onnistuneesti`)
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const resource = utils[resourceName as keyof typeof utils] as any
          resource.list.invalidate()
          // Invalidate related resources
          invalidateRelatedResources(utils)
          return result
        },
        isPending: baseMutation.isPending,
        error: baseMutation.error,
      } as UseMutationReturn<{ data: TCreateInput }, TItem>
    },

    useUpdate: (): UseMutationReturn<
      { id: number; data: TUpdateInput },
      TItem
    > => {
      const utils = trpc.useUtils()
      const baseMutation = resource.update.useMutation({
        onError: async (error: Error) => {
          await showErrorNotification(error, 'päivitys')
        },
      })

      return {
        mutateAsync: async (input, options = { showNotifications: true }) => {
          const result = await baseMutation.mutateAsync(input)
          if (options.showNotifications !== false) {
            await showSuccessNotification(`${label} päivitetty onnistuneesti`)
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const resource = utils[resourceName as keyof typeof utils] as any
          resource.list.invalidate()
          // Directly update the cache with the returned data instead of invalidating
          resource.getById.setData({ id: (result as HasId).id }, result)
          // Invalidate related resources
          invalidateRelatedResources(utils)
          return result
        },
        isPending: baseMutation.isPending,
        error: baseMutation.error,
      } as UseMutationReturn<{ id: number; data: TUpdateInput }, TItem>
    },

    useDelete: (): UseMutationReturn<{ id: number }, TItem> => {
      const utils = trpc.useUtils()
      const baseMutation = resource.delete.useMutation({
        onError: async (error: Error) => {
          await showErrorNotification(error, 'poisto')
        },
      })

      return {
        mutateAsync: async (input, options = { showNotifications: true }) => {
          const result = await baseMutation.mutateAsync(input)
          if (options.showNotifications !== false) {
            await showSuccessNotification(`${label} poistettu onnistuneesti`)
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const resource = utils[resourceName as keyof typeof utils] as any
          resource.list.invalidate()
          // Remove the deleted item from cache (soft deletes return 404 from getById)
          resource.getById.setData({ id: (result as HasId).id }, undefined)
          // Invalidate related resources
          invalidateRelatedResources(utils)
          return result
        },
        isPending: baseMutation.isPending,
        error: baseMutation.error,
      } as UseMutationReturn<{ id: number }, TItem>
    },
  } as const
}
